import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import Switch from '@mui/material/Switch';
import { localurl } from '../components/utils';
import toast, { Toaster } from "react-hot-toast";

const Settings = () => {
  const [isTimeEnabled, setIsTimeEnabled] = useState(false);
  const [initialValues, setInitialValues] = useState({
    bot_name: '',
    welcome_message: '',
    offline_message: '',
    timezone: '',
    Operational_hours: false,
    start_time: '',
    end_time: '',
    auto_reply: true,
    human_handoff: true,
  });

  const validationSchema = Yup.object({
    bot_name: Yup.string(),
    welcome_message: Yup.string(),
    offline_message: Yup.string(),
    Operational_hours: Yup.boolean(),
    start_time: Yup.string(),
    end_time: Yup.string(),
    auto_reply: Yup.boolean(),
    human_handoff: Yup.boolean(),
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${localurl}/settings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        setInitialValues({
          bot_name: data.bot_name || '',
          welcome_message: data.welcome_message || '',
          offline_message: data.offline_message || '',
          Operational_hours: data.Operational_hours || false,
          start_time: data.start_time || '',
          end_time: data.end_time || '',
          auto_reply: data.auto_reply ?? true,
          human_handoff: data.human_handoff ?? true,
        });

        setIsTimeEnabled(data.Operational_hours);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, []);


  const handleSubmit = async (values) => {
    try {
      const response = await fetch(`${localurl}/settings`, {
        method: 'POST', 
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(values),
      });

      const result = await response.json();
      toast.success('Settings updated:', result);
    } catch (error) {
      toast.error('Error updating settings:', error);
    }
  };

  if (!initialValues) {
    return <div className="text-gray-500 text-sm">Loading...</div>;
  }

  return (
    <div className='w-full'>
      <h1 className="text-black font-bold text-2xl mb-3">General Settings</h1>
      <p className="font-normal text-base text-gray-600 mb-10">
        Configure the basic settings for your AI chatbot.
      </p>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form>
            {/* Bot Name */}
            <div className='flex flex-row justifay justify-between mt-8 '>
              <div className='w-full md:w-1/3'>
                <label className='text-sm font-medium text-gray-800'>Bot Name</label>
                <Field type="text" name="bot_name" className="border-2 text-sm border-gray-200 p-2 rounded-lg mt-1 w-full" />
                <p className='text-xs font-medium mt-2 text-gray-400'>This is how your bot will introduce itself to users.</p>
                <ErrorMessage name="bot_name" component="div" className="text-red-500 text-xs" />
              </div>
            </div>

            {/* Welcome message */}
            <div className='w-full mt-6'>
              <label className='text-sm font-medium text-gray-800'>Welcome message</label>
              <Field as="textarea" name="welcome_message" className="border-2 border-gray-200 text-sm p-2 rounded-lg mt-1 w-full" />
              <p className='text-xs font-medium mt-2 text-gray-400'>Displayed when a user first interacts with your bot.</p>
              <ErrorMessage name="welcome_message" component="div" className="text-red-500 text-xs" />
            </div>

            {/* Offline message */}
            <div className='w-full mt-6'>
              <label className='text-sm font-medium text-gray-800'>Offline message</label>
              <Field as="textarea" name="offline_message" className="border-2 border-gray-200 text-sm p-2 rounded-lg mt-1 w-full" />
              <p className='text-xs font-medium mt-2 text-gray-400'>Displayed when your bot is offline.</p>
              <ErrorMessage name="offline_message" component="div" className="text-red-500 text-xs" />
            </div>

            {/* Operating Hours */}
            <div className="flex flex-row justify-between items-center mt-6 border-2 border-gray-200 p-2 rounded-lg">
              <div className='flex flex-col'>
                <span className="text-sm">Operating Hours</span>
                <p className='text-xs font-medium mt-2 text-gray-400'>Set specific hours when your chatbot is available.</p>
              </div>
              <Switch
                checked={values.Operational_hours}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setIsTimeEnabled(checked);
                  setFieldValue('Operational_hours', checked);

                  if (!checked) {
                    setFieldValue('start_time', '');
                    setFieldValue('end_time', '');
                  }
                }}
              />
            </div>

            {/* Start & End Time */}
            {isTimeEnabled && (
              <div className='flex flex-row gap-6'>
                <div className='mt-6 w-1/2'>
                  <label className='text-sm font-medium text-gray-800'>Start Time</label>
                  <Field type="time" name="start_time" className="border-2 border-gray-200 text-sm p-2 rounded-lg mt-1 w-full" />
                  <ErrorMessage name="start_time" component="div" className="text-red-500 text-xs" />
                </div>
                <div className='mt-6 w-1/2'>
                  <label className='text-sm font-medium text-gray-800'>End Time</label>
                  <Field type="time" name="end_time" className="border-2 border-gray-200 text-sm p-2 rounded-lg mt-1 w-full" />
                  <ErrorMessage name="end_time" component="div" className="text-red-500 text-xs" />
                </div>
              </div>
            )}

            {/* Auto Reply & Human Handoff */}
            {/* <div className='md:flex-row flex flex-col justify-between md:gap-6'>
              <div className="flex flex-row justify-between items-center mt-6 border-2 border-gray-200 p-2 rounded-lg md:w-1/2">
                <div className='flex flex-col'>
                  <span className="text-sm">Auto Reply</span>
                  <p className='text-xs font-medium mt-2 text-gray-400'>Automatically respond to common questions.</p>
                </div>
                <Switch
                  checked={values.auto_reply}
                  onChange={(e) => setFieldValue('auto_reply', e.target.checked)}
                />
              </div>
              <div className="flex flex-row justify-between items-center mt-6 border-2 border-gray-200 p-2 rounded-lg md:w-1/2">
                <div className='flex flex-col'>
                  <span className="text-sm">Human Handoff</span>
                  <p className='text-xs font-medium mt-2 text-gray-400'>Allow escalation to human agents.</p>
                </div>
                <Switch
                  checked={values.human_handoff}
                  onChange={(e) => setFieldValue('human_handoff', e.target.checked)}
                />
              </div>
            </div> */}

            {/* Submit */}
            <div className='mt-6 flex justify-end'>
              <button type="submit" className='px-8 py-3 bg-green text-white text-sm rounded-lg'>Save Changes</button>
            </div>
          </Form>
        )}
      </Formik>
      <Toaster />
    </div>
  );
};

export default Settings;
